import { type FC } from 'react'

import { NumberFormatter, UnitLabel } from '@/components/elements'
import { EMPTY_LABEL } from '@/constants'
import { UNITS } from '@/constants/common/message.constants'
import { useWeightDisplayFormat } from '@/features/dcs/hooks'
import { convertKgToWeightDisplayUnit } from '@/libs/utils/weight-display-format'

type Props = {
  /** Kg単位の数値 */
  value: number | null
  /**
   * 単位（t / kg）に当てる class。数値と単位で太さを変えたい呼び出し側のために開けている。
   * 未指定なら呼び出し側の太さをそのまま継ぐ
   */
  unitClassName?: string
}

/**
 * 荷物重量(kg)を、選択中拠点の環境設定（単位 t/kg・小数桁数）に従ってフォーマットする（Issue #4905）。
 *
 * KgToTonFormatter + <UnitLabel>t</UnitLabel> の組が呼び出し側 17 箇所に分散しており、
 * 単位を DC 単位で切り替え可能にすると「数値の変換」と「単位表記」を常に同時に更新する必要がある。
 * 分離したまま単位を可変にすると片方だけ更新し忘れる事故が起きやすいため、1 コンポーネントに束ねる。
 *
 * 車両の最大積載量・車両総重量（KgToTonFormatter 使用 6 箇所）は本コンポーネントの対象外。
 * 引き続き固定 t 表示の KgToTonFormatter を使う。
 */
export const CargoWeightFormatter: FC<Props> = ({ value, unitClassName }) => {
  const { unit, fractionDigits } = useWeightDisplayFormat()

  if (value == null) return EMPTY_LABEL

  const displayValue = convertKgToWeightDisplayUnit(value, unit)
  return (
    <>
      <NumberFormatter
        value={displayValue}
        option={{
          minimumFractionDigits: fractionDigits,
          maximumFractionDigits: fractionDigits
        }}
      />
      <UnitLabel className={unitClassName}>{UNITS.MASS[unit]}</UnitLabel>
    </>
  )
}
